import * as React from 'react'
import { ColumnDef, Table, FilterOptions, PaginationOptions, Checkbox } from 'cs-kevlar-react'
import { User, EditableUser, CustomerAdmin } from '../../contexts/base'
import { determineDefaultEmail, determineDefaultPhone } from 'utils/users'
import { UserDisplayName } from 'components/UserDisplayName'
import { Alert } from '@mui/material';
import { AddMultipleUsersDetails } from '.'
import { EditModalActionBar } from '..'

interface AddMultipleUsersProps {
  onClose: () => void
  availableLicenses: number
  availableUsers: User[]
  activeAdminUsers: CustomerAdmin[]
  onAdd: (values: EditableUser[]) => void
}

const FILTER_OPTIONS: Partial<FilterOptions> = {
  filterable: true,
  disableLiveFiltering: true,
  enableSuggestions: true,
  enableMultipleSearchTerms: true,
}

const PAGINATION_OPTIONS: Partial<PaginationOptions> = {
  hidePaginationBar: true,
  disablePaging: true
}

export const AddMultipleUsers: React.FunctionComponent<AddMultipleUsersProps> = ({
  onClose,
  availableUsers,
  availableLicenses,
  activeAdminUsers,
  onAdd,
}) => {

  const columns: ColumnDef<User, string>[] = [
  {
      label: '',
      sortable: false,
      sortByDefault: false,
      filterable: false,
      component: ({ row }) => 
        <Checkbox
          key={"UserSelected"+row.id}
          defaultChecked={false} 
          checked={selectedUserIds.includes(row.id)} 
          disabled={showLicenseWarning && !selectedUserIds.includes(row.id)} 
          onChange={(event)=> {handleCheck(event, row)}}
        />,
      width: 10,
    },
    {
      label: 'Name',
      sortable: true,
      sortByDefault: true,
      sortValueFunc: (row: User) => row.fullName,
      filterable: true,
      dataProperty: 'fullName' as keyof User,
    },
    {
      label: 'Username',
      sortable: true,
      sortByDefault: false,
      sortValueFunc: (row: User) => row.name,
      filterable: true,
      component: ({ row }) => 
        <UserDisplayName 
          key={"Username"+row.id}
          user={row}
        />,
      filterFunc: (row: User) => row.name,
    },
  ]

  const handleCheck = (chkValue: any, row: User) => {

    if (chkValue.target.checked)
    {
      const selectedIds = [...selectedUserIds, row.id]
      setSelectedUserIds(selectedIds)
      setDisableNextButton(false)

      if (selectedIds.length >= availableLicenses)
        setShowLicenseWarning(true)
    }
    else 
    {
      const selectedIds = selectedUserIds.filter(item => item !== row.id)
      setSelectedUserIds(selectedIds)

      if (selectedIds.length == 0)
        setDisableNextButton(true)
      
      if (selectedIds.length < availableLicenses)
        setShowLicenseWarning(false)
    }
  }

  const initialSelectableUsers = () => {
    const users = availableUsers.map(user => {
      var adminUser = {} as CustomerAdmin
      const matchedAdminUsers = activeAdminUsers.filter(adminUser => adminUser.userId === user.id)
      if (matchedAdminUsers.length > 0) adminUser = matchedAdminUsers[0]
      
      return {
        ...user,
        phone: adminUser.phone ? adminUser.phone : determineDefaultPhone(user),
        email: adminUser.email ? adminUser.email : determineDefaultEmail(user),
        selected: false
      }
    })

    return users
  }

  const [selectableUsers, setSelectableUsers] = React.useState<User[]>(initialSelectableUsers)
  const [disableNextButton, setDisableNextButton] = React.useState(true)
  const [showLicenseWarning, setShowLicenseWarning] = React.useState(false)
  const [showDetails, setShowDetails] = React.useState(false)
  const [editableUsers, setEditableUsers] = React.useState<EditableUser[]>([])
  const [selectedUserIds, setSelectedUserIds] = React.useState<string[]>([])

  const onSelectAll = () => {
    let userIds: string[] = []
    // If all of them are selected, then deselect them all. If they aren't all selected, select as many
    // as possible up until the license limit.
    if(selectedUserIds.length < availableLicenses && selectedUserIds.length < selectableUsers.length)
    {
      const unselectedUserIds = selectableUsers.filter(x => !selectedUserIds.includes(x.id)).map(x => x.id)
      const licensesLeft = availableLicenses - selectedUserIds.length
      const userIdsToSelect = unselectedUserIds.slice(0, licensesLeft)
      userIds = selectedUserIds.concat(userIdsToSelect)
    }
    setSelectedUserIds(userIds)
    setShowLicenseWarning(userIds.length >= availableLicenses)
    setDisableNextButton(userIds.length == 0)
  }

  const onSelectUsers = () => {
    var selectedSelectableUsers = selectableUsers.filter((u) => selectedUserIds.includes(u.id))

    setEditableUsers(selectedSelectableUsers.map((user) =>
      {
        var selectedUser: EditableUser = {
          ...user,
          externalPhoneList: user.phones?.map(x => x.number) ?? [],
          externalEmailList: user.emailAddresses?.map(x => x.email) ?? [],
        }

        return selectedUser
      }))

    if (selectedSelectableUsers.length > 0)
      setShowDetails(true)
  }

  return (
    <React.Fragment>
      {!showDetails && showLicenseWarning && 
        <Alert variant="filled" severity="warning">
          No licenses available.
        </Alert>
      }
      {!showDetails &&
        <Table
          data={selectableUsers}
          filterOptions={FILTER_OPTIONS}
          paginationOptions={PAGINATION_OPTIONS}
          columns={columns as ColumnDef<User, string>[]}
          ariaLabel="Users to Add"
          useDefaultRow
          uniqueIdentifier="AddMultipleUsersList"
        />
      }
      {!showDetails &&
        <EditModalActionBar
          canDelete={true}
          deleteText={selectedUserIds.length == selectableUsers.length || selectedUserIds.length == availableLicenses ? 'Unselect All' : 'Select All'}
          onDelete={onSelectAll}
          onCancel={onClose}
          onSave={onSelectUsers}          
          canSubmit={!disableNextButton}
          saveButtonType='button'
          saveText='Next'
          />
      }
      {showDetails &&
        <AddMultipleUsersDetails
          onAdd={onAdd}
          onClose={onClose}
          users={editableUsers}
        />
      }
    </React.Fragment>
  )
}
